import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import PropTypes from 'prop-types'

import curvedArrowIcon from './curved-arrow-icon.svg'
import {
  StyledButtonRow,
  StyledClinButton,
  StyledContainer,
  StyledDescription,
  StyledIcon,
  StyledNotFound,
  StyledNotFoundButton,
  StyledNotFoundContainer,
  StyledPageHeader,
  StyledSearchContainer,
  StyledTitle,
  StyledTitleRow,
  StyledTitleValue,
  StyledRecentSearches,
  StyledRecentSearchesLabel
} from './Homepage.styles'
import { HomepageLypBanner } from './HomepageLypBanner'
import lightningIcon from './lightning-icon.svg'
import { ClinTheme } from '../../ClinTheme'
import { ClinButton } from '../../components/ClinButton'
import { ClinSpacer } from '../../components/ClinSpacer'
import { ClinText } from '../../components/ClinText'
import { TypographyVariant } from '../../components/ClinText/ClinText.styles'
import { AutoSuggest } from '../../features/Search/AutoSuggest'
import { GlobalSearchAUS } from '../../features/Search/GlobalSearch/GlobalSearchAUS'
import { ClinAnnounceBar } from '../../components/ClinAnnounceBar'
import { AnnounceMode } from '../../components/ClinAnnounceBar/ClinAnnounceBar'
import { getBanner } from '../../services/ApiService'
import { IBanner } from '../../types/IBanner'
import { useOnMount } from '../../utils/useOnMount'
import config from '../../services/ConfigProvider'
import {
  StyledOrdersAnnounce,
  StyledOrdersAnnounceCta,
  StyledOrdersAnnounceText
} from '../OrderPages/Orders/Orders.styles'
import { ClinIcon } from '../../components/ClinIcon'
import { ClinIconPathName } from '../../components/ClinIcon/ClinIcon.paths'
import { CatalogDtoSuggestionDto } from '../../types/swaggerTypes'
import { FeatureFlagKeys } from '../../context/featureFlags/FeatureFlagKeys'
import { useFeatureFlags } from '../../context/featureFlags/FeatureFlagContext'
import { AutoSuggestOld } from '../../features/Search/AutoSuggest/old/AutoSuggestOld'
import { GlobalSearchAUSOld } from '../../features/Search/GlobalSearch/old/GlobalSearchAUSOld'

export const Homepage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const [recentSearches, setRecentSearches] = useState<
    CatalogDtoSuggestionDto[]
  >([])
  const [banner, setBanner] = useState<IBanner>()
  const [isNotFound, setIsNotFound] = useState<boolean>(false)
  const [isAliasesDisplayed, setIsAliasesDisplayed] = useState<boolean>(false)
  const [currentQuery, setCurrentQuery] = useState<string>('')

  useEffect(() => {
    const loadRecentSearches = () => {
      const storedSearches = JSON.parse(
        localStorage.getItem('recentSearches') || '[]'
      )

      // Filter to only include valid CatalogDtoSuggestionDto objects
      // This provides backward compatibility with old storage format
      const validRecentSearches = storedSearches.filter((search: any) => {
        return (
          search.document &&
          (search.document.catalogDocumentId || search.document.programId)
        )
      })

      setRecentSearches(
        validRecentSearches.length > 0 ? validRecentSearches : []
      )
    }

    // Load initial recent searches
    loadRecentSearches()

    // Listen for custom event to sync recent searches within the same tab
    const handleRecentSearchesUpdate = () => {
      loadRecentSearches()
    }

    window.addEventListener('recentSearchesUpdated', handleRecentSearchesUpdate)

    return () => {
      window.removeEventListener(
        'recentSearchesUpdated',
        handleRecentSearchesUpdate
      )
    }
  }, [])

  useOnMount(() => {
    if (config.showBanner === 'true' || config.showBanner === true) {
      void getBanner().then((result) => {
        try {
          // Get data from squidEx dict using language as key accessor
          const bannerObj = result.data?.items[0].data.banner[
            i18n.language
          ] as IBanner
          setBanner(bannerObj)
        } catch (e) {
          console.warn('An error occurred fetching the banner:', e)
        }
      })
    }
  })

  const handleViewOrders = () => {
    history.push('/orders')
  }

  const handleViewPatients = () => {
    history.push('/patients')
  }

  const handleRequestDrug = () => {
    history.push('/sourcing-enquiry')
  }

  const handleViewProducts = () => {
    history.push('/products/catalogue')
  }
  const { useFeatureFlag } = useFeatureFlags()
  const enableNewGlobalSearch = useFeatureFlag(FeatureFlagKeys.NewGlobalSearch)
  const handleRecentSearchClick = (recentSearch: CatalogDtoSuggestionDto) => {
    // Navigate to the appropriate page based on the search type
    if (recentSearch.document.derivedFrom === 'ManagedAccessProgram') {
      history.push(
        `/programs/access-programs/${recentSearch.document.programId}`
      )
    } else {
      history.push(`/product/${recentSearch.document.catalogDocumentId}`)
    }
  }

  const handleKeyDown = (
    e: React.KeyboardEvent,
    recentSearch: CatalogDtoSuggestionDto
  ) => {
    // Handle Enter or Space key press
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleRecentSearchClick(recentSearch)
    }
  }

  return (
    <StyledContainer>
      {banner && (
        <ClinAnnounceBar
          title={' '}
          mode={AnnounceMode.Information}
          marginBottom={ClinTheme.space[4]}
        >
          <StyledOrdersAnnounce>
            <StyledOrdersAnnounceText>
              <strong>{banner.title}</strong>
              <br />
              {banner.description}
            </StyledOrdersAnnounceText>
            {banner.buttonTitle && banner.buttonUrl && (
              <StyledOrdersAnnounceCta>
                <a
                  href={banner.buttonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ClinButton as="div" variant="primary">
                    {banner.buttonTitle}
                  </ClinButton>
                </a>
              </StyledOrdersAnnounceCta>
            )}
          </StyledOrdersAnnounce>
        </ClinAnnounceBar>
      )}
      <HomepageLypBanner />
      <StyledPageHeader>
        <div style={{ position: 'relative' }}>
          <ClinText
            as="h1"
            color={ClinTheme.colors.black}
            fontWeight={ClinTheme.fontWeights.bolder}
            fontSize={ClinTheme.fontSizes[6]}
            lineHeight={ClinTheme.lineHeights.body}
            marginTop={ClinTheme.space[7]}
          >
            {t('product_catalogue:aus_title')}
            <StyledIcon
              width={30}
              height={38}
              src={lightningIcon}
              style={{ position: 'absolute', top: '55px', right: '-41px' }}
              className="lightening-icon"
            />
          </ClinText>
          <StyledIcon
            width={104}
            height={63}
            src={curvedArrowIcon}
            style={{ position: 'absolute', top: '80px', right: '-158px' }}
          />
        </div>
        <ClinSpacer height={15} />
        <ClinText
          className="description"
          variant={TypographyVariant.LargerParagraph}
          color={ClinTheme.colors.black}
        >
          <Trans
            i18nKey="product_catalogue:aus_description"
            components={{ bold: <strong /> }}
          />
        </ClinText>
      </StyledPageHeader>
      <ClinSpacer height={14} />
      <StyledSearchContainer>
        {enableNewGlobalSearch ? (
          <AutoSuggest
            debounceTimeMs={300}
            noResultsText={t('global_search:suggestion_text_aus')}
            maUserNoResultsText={t('global_search:ma_user_no_results_text')}
            suggestionText={t('global_search:suggestion_text_aus')}
            noResultsPath="/products/catalogue?q=*"
            suggestionTextColor={ClinTheme.colors.primaryMid}
            suggestionTextPadding={`${ClinTheme.space[3]}px`}
            borderRadiusLastLiElement="28"
            setIsNotFound={setIsNotFound}
            setIsAliasesDisplayed={setIsAliasesDisplayed}
            isAliasesDisplayed={isAliasesDisplayed}
            recentSearches={recentSearches}
            setCurrentQuery={setCurrentQuery}
            showNoResults={false}
            isShowExactMatch={true}
            searchComponent={(props) => (
              <GlobalSearchAUS
                {...props}
                handleChange={(e) => {
                  props.handleChange?.(e)
                }}
                handleEnter={(e) => {
                  props.handleEnter?.(e)
                }}
                handleFocus={() => {
                  props.handleFocus?.()
                }}
                handleBlur={() => {
                  props.handleBlur?.()
                }}
              />
            )}
          />
        ) : (
          <AutoSuggestOld
            noResultsText={t('global_search:suggestion_text_aus')}
            maUserNoResultsText={t('global_search:ma_user_no_results_text')}
            suggestionText={t('global_search:suggestion_text_aus')}
            noResultsPath="/products/catalogue?q=*"
            suggestionTextColor={ClinTheme.colors.primaryMid}
            suggestionTextPadding={`${ClinTheme.space[3]}px`}
            borderRadiusLastLiElement="28"
            setIsNotFound={setIsNotFound}
            recentSearches={recentSearches}
            setCurrentQuery={setCurrentQuery}
            showNoResults={true}
            searchComponent={(props) => (
              <GlobalSearchAUSOld
                {...props}
                handleChange={(e) => {
                  props.handleChange?.(e)
                }}
                handleEnter={(e) => {
                  props.handleEnter?.(e)
                }}
                handleFocus={() => {
                  props.handleFocus?.()
                }}
                handleBlur={() => {
                  props.handleBlur?.()
                }}
              />
            )}
          />
        )}
      </StyledSearchContainer>
      {isNotFound ? (
        <StyledNotFound>
          <StyledNotFoundContainer>
            <StyledTitleRow>
              <StyledTitle
                as="span"
                variant={TypographyVariant.LargerParagraph}
                color={ClinTheme.colors.black}
              >
                {t('home:not_found_title')}
              </StyledTitle>
              <StyledTitleValue
                as="span"
                variant={TypographyVariant.LargerParagraph}
                color={ClinTheme.colors.black}
              >
                &#39;{currentQuery}&#39;
              </StyledTitleValue>
            </StyledTitleRow>
            <StyledDescription as="div" color={ClinTheme.colors.black}>
              {t('home:not_found_description')}
            </StyledDescription>
            <StyledButtonRow>
              <ClinButton
                type="button"
                variant="primary"
                onClick={handleRequestDrug}
              >
                {t('home:request_drug')}
              </ClinButton>
              <StyledNotFoundButton
                type="button"
                variant="secondary"
                onClick={handleViewProducts}
              >
                {t('home:view_products')}
                <ClinIcon
                  iconName={ClinIconPathName.ArrowRight}
                  iconSize={20}
                  style={{ marginLeft: ClinTheme.space[2] }}
                />
              </StyledNotFoundButton>
            </StyledButtonRow>
          </StyledNotFoundContainer>
        </StyledNotFound>
      ) : (
        <>
          {recentSearches.length > 0 && (
            <StyledRecentSearches>
              <div className="recent-searches-row">
                <StyledRecentSearchesLabel>
                  {t('home:recent_searches')}
                </StyledRecentSearchesLabel>
                <div className="first-line">
                  {recentSearches.slice(0, 3).map((item, index) => (
                    <span
                      key={index}
                      className="button-text"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleRecentSearchClick(item)}
                      onKeyDown={(e) => handleKeyDown(e, item)}
                    >
                      {item.document.catalogItemName}
                    </span>
                  ))}
                </div>
              </div>
            </StyledRecentSearches>
          )}
          <ClinSpacer height={80} className="clin-spacer-recent-searches" />

          <StyledButtonRow>
            <StyledClinButton
              type="button"
              variant="secondary"
              onClick={handleViewOrders}
            >
              {t('common:buttons.view_all_orders')}
            </StyledClinButton>
            <ClinButton
              type="button"
              variant="primary"
              onClick={handleViewPatients}
              style={{ textTransform: 'none', width: '258px', height: '48px' }}
            >
              {t('common:buttons.view_all_patients')}
            </ClinButton>
          </StyledButtonRow>
        </>
      )}
    </StyledContainer>
  )
}

Homepage.propTypes = {
  handleChange: PropTypes.func,
  handleEnter: PropTypes.func,
  handleFocus: PropTypes.func,
  handleBlur: PropTypes.func
}
